// src/pages/TermsPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TermsPage: React.FC = () => {
  const { t } = useTranslation("legal");

  return (
    <div className="legal-root">
      <header className="lh">
        <h1>{t("terms.title", "이용약관")}</h1>
        <Link className="back" to="/signal-intro">
          {t("back", "← 인트로로 돌아가기")}
        </Link>
      </header>

      {/* 1. 서비스 성격 및 범위 */}
      <section className="card">
        <h2>{t("terms.use", "서비스 이용")}</h2>
        <p>
          {t(
            "terms.use.body",
            "본 서비스는 시장 데이터·차트 도구 및 학습 자료를 제공하는 정보 서비스이며, 투자 자문·중개·권유가 아닙니다. 모든 의사결정과 책임은 사용자에게 있습니다."
          )}
        </p>
        <ul className="bullets">
          <li>
            {t(
              "terms.use.scope1",
              "제공 콘텐츠는 교육/연구 목적의 일반 정보이며 특정 종목의 매수·매도나 수익 보장을 의미하지 않습니다."
            )}
          </li>
          <li>
            {t(
              "terms.use.scope2",
              "사용자는 현지 법규·거래소 약관을 준수해야 하며, 레버리지/파생상품은 원금 손실 위험이 매우 큽니다."
            )}
          </li>
        </ul>
      </section>

      {/* 2. 시장 데이터 및 정확성 */}
      <section className="card">
        <h2>{t("terms.data", "시장데이터")}</h2>
        <p>
          {t(
            "terms.data.body",
            "시세·지표·알림 등 데이터는 외부 공급망/네트워크 상황에 따라 지연·누락·오류가 발생할 수 있으며 정확성·완전성·연속성이 보장되지 않습니다."
          )}
        </p>
        <ul className="bullets">
          <li>
            {t(
              "terms.data.delay",
              "표시 가격은 거래소 체결가와 다를 수 있습니다. 실제 주문 전 거래소의 실시간 호가/체결을 반드시 확인하세요."
            )}
          </li>
          <li>
            {t(
              "terms.data.thirdparty",
              "일부 데이터·광고·SDK는 제3자에 의해 제공되며, 해당 사업자의 정책 변경으로 예고 없이 중단·변경될 수 있습니다."
            )}
          </li>
        </ul>
      </section>

      {/* 3. 책임 제한 */}
      <section className="card">
        <h2>{t("terms.liability", "책임 제한")}</h2>
        <p>
          {t(
            "terms.liability.body",
            "서비스 제공자는 본 서비스의 이용 또는 이용 불능으로 인한 직접·간접·우발·특별·결과적 손해 및 데이터 손실에 대해 법이 허용하는 범위 내에서 책임을 지지 않습니다."
          )}
        </p>
        <ul className="bullets">
          <li>
            {t(
              "terms.liability.risk",
              "사용자는 자산 가격 변동, 시스템 장애, 네트워크 문제 등의 위험을 인지하고 이를 부담합니다."
            )}
          </li>
          <li>
            {t(
              "terms.liability.noGuarantee",
              "서비스는 ‘있는 그대로’ 제공되며 수익·정확성·가용성에 관한 명시적/묵시적 보증을 하지 않습니다."
            )}
          </li>
        </ul>
      </section>

      {/* 4. 광고/해금(광고 기반) */}
      <section className="card">
        <h2>{t("terms.ads", "광고 및 해금")}</h2>
        <p>
          {t(
            "terms.ads.body",
            "본 앱은 광고 기반(Ad-supported)으로 제공됩니다. 광고 시청 또는 제휴 프로모션을 통해 일부 기능이 일정 시간 해금될 수 있습니다. 광고 노출 여부·빈도·보상은 광고사 정책, 지역, 디바이스 환경에 따라 달라질 수 있습니다."
          )}
        </p>
        <ul className="bullets">
          <li>
            {t(
              "terms.ads.limit",
              "광고 재생 실패·중단 시 보상이 제공되지 않을 수 있습니다. 반복 새로고침·자동화 재생 등 부정 행위가 탐지되면 보상 및 이용이 제한될 수 있습니다."
            )}
          </li>
          <li>
            {t(
              "terms.ads.change",
              "광고 기반 해금 정책은 사전 고지 없이 수정·중단될 수 있습니다."
            )}
          </li>
        </ul>
      </section>

      {/* 5. 요금/결제 없음 명시 */}
      <section className="card">
        <h2>{t("terms.free", "요금/결제 없음")}</h2>
        <p>
          {t(
            "terms.free.body",
            "우리는 유료 요금제·유료 구독·인앱결제를 제공하지 않습니다. 사용자는 어떠한 대가도 지불하지 않고, 광고 시청을 통해 기능을 이용합니다."
          )}
        </p>
      </section>

      {/* 6. 이용자 의무 및 금지행위 */}
      <section className="card">
        <h2>{t("terms.user", "이용자 의무 및 금지행위")}</h2>
        <ul className="bullets">
          <li>
            {t(
              "terms.user.law",
              "관련 법령·거래소 정책·본 약관을 준수하고, 계정/디바이스 보안을 유지합니다."
            )}
          </li>
          <li>
            {t(
              "terms.user.prohibit1",
              "리버스엔지니어링·자동화 스크래핑·비정상 트래픽 유발 등 남용을 금지합니다."
            )}
          </li>
          <li>
            {t(
              "terms.user.prohibit2",
              "허위 정보 유포, 권리 침해, 불법 거래 유도, 개인정보 무단 수집 등 위법·부정 사용을 금지합니다."
            )}
          </li>
        </ul>
      </section>

      {/* 7. 지식재산권 */}
      <section className="card">
        <h2>{t("terms.ip", "지식재산권")}</h2>
        <p>
          {t(
            "terms.ip.body",
            "서비스 내 텍스트·그래픽·코드·로고 등 콘텐츠와 소프트웨어의 권리는 제공자 또는 라이선스 보유자에게 있으며, 사전 서면 동의 없이 복제·배포·개작·영리 이용할 수 없습니다."
          )}
        </p>
      </section>

      {/* 8. 제3자 링크 */}
      <section className="card">
        <h2>{t("terms.links", "제3자 링크")}</h2>
        <p>
          {t(
            "terms.links.body",
            "서비스에는 제3자 웹사이트·광고·콘텐츠로 연결되는 링크가 포함될 수 있으며, 당사는 외부 사이트의 조건·정책·콘텐츠에 대해 책임을 지지 않습니다."
          )}
        </p>
      </section>

      {/* 9. 변경·중단 */}
      <section className="card">
        <h2>{t("terms.change", "약관 변경 및 서비스 중단")}</h2>
        <p>
          {t(
            "terms.change.body",
            "운영상 또는 법령상 필요 시 약관·정책·기능을 변경하거나 전부/일부 서비스를 중단할 수 있습니다. 중요한 변경은 합리적인 방법으로 고지합니다."
          )}
        </p>
      </section>

      {/* 10. 준거법/분쟁 */}
      <section className="card">
        <h2>{t("terms.law", "준거법 및 분쟁 해결")}</h2>
        <p>
          {t(
            "terms.law.body",
            "본 약관은 제공자의 소재지 법을 준거법으로 하며, 분쟁은 관할 법원에서 해결합니다. 강행 규정이 있는 경우 그 규정을 우선 적용합니다."
          )}
        </p>
      </section>

      {/* 11. 문의 */}
      <section className="card">
        <h2>{t("terms.contact", "문의처")}</h2>
        <p>
          {t(
            "terms.contact.body",
            "약관·개인정보·광고 관련 문의: support@yourapp.example (평일 10:00–18:00, 공휴일 제외)"
          )}
        </p>
      </section>

      <footer className="lf">
        <Link to="/privacy">{t("privacy", "개인정보처리방침")}</Link>
        <span>·</span>
        <Link to="/signal-intro">{t("intro", "인트로")}</Link>
      </footer>

      <style>{`
        .legal-root{min-height:100dvh;background:#0b0f16;color:#eaeaea;padding:20px}
        .lh{display:flex;justify-content:space-between;align-items:center;gap:8px;max-width:960px;margin:0 auto 12px}
        .lh h1{font-size:20px;font-weight:900;margin:0}
        .back{color:#9ec1ff;text-decoration:none}
        .card{max-width:960px;margin:10px auto;border:1px solid #ffffff22;background:#111;border-radius:12px;padding:14px}
        .card h2{margin:0 0 6px;font-size:16px;font-weight:900}
        .card p{margin:0;color:#cbd5e1;line-height:1.7}
        .bullets{margin:6px 0 0 16px;padding:0;display:grid;gap:4px}
        .bullets li{list-style:disc;color:#cbd5e1}
        .lf{max-width:960px;margin:16px auto 0;display:flex;gap:10px;align-items:center;color:#bcd3ff}
        .lf a{color:#bcd3ff;text-decoration:none;border-bottom:1px solid transparent}
        .lf a:hover{border-bottom-color:#bcd3ff}
      `}</style>
    </div>
  );
};

export default TermsPage;
